import { z } from 'zod';
import { paginationMetaSchema } from '@/api/pagination';
import { lookupItemSchema } from '@/types/lookupItem.types';
import {
  optionalDateSchema,
  optionalPositiveIdSchema,
  optionalTextSchema,
} from '@/utils/searchParams';
import {
  INSTITUTE_CATEGORY,
  INSTITUTE_LEVEL,
  REMOVAL_REASON,
  REMOVAL_SUBMISSION_TYPES,
  SUBMISSION_STATUS,
  SUBMISSION_TYPE,
  UPDATE_SUBMISSION_TYPE,
} from '../constants/instituteSubmission.constants';

export type SubmissionStatus = (typeof SUBMISSION_STATUS)[number];
export type SubmissionType = (typeof SUBMISSION_TYPE)[number];

const IMPORTED_NULL_MARKER = 'NULL';

const nullableImportedTextSchema = z
  .string()
  .nullable()
  .transform((text) => (text?.trim().toUpperCase() === IMPORTED_NULL_MARKER ? null : text));

export const instituteSubmissionSearchSchema = z.object({
  submission: optionalPositiveIdSchema,
  search: optionalTextSchema,
  reviewStatus: z.enum(SUBMISSION_STATUS).optional().catch(undefined),
  submissionType: z.enum(SUBMISSION_TYPE).optional().catch(undefined),
  instituteTypeId: optionalPositiveIdSchema,
  districtId: optionalPositiveIdSchema,
  thanaId: optionalPositiveIdSchema,
  amUserId: optionalPositiveIdSchema,
  zmUserId: optionalPositiveIdSchema,
  dateFrom: optionalDateSchema,
  dateTo: optionalDateSchema,
});

export const instituteSubmissionListItemSchema = z.object({
  id: z.coerce.number(),
  submissionType: z.enum(SUBMISSION_TYPE),
  status: z.enum(SUBMISSION_STATUS),
  instituteName: nullableImportedTextSchema,
  code: z.string().nullable(),
  eiinNumber: nullableImportedTextSchema,
  instituteTypeName: z.string().nullable(),
  thanaName: z.string().nullable(),
  districtName: z.string().nullable(),
  userId: z.coerce.number(),
  userName: z.string(),
  hrEmpId: z.string().nullable(),
  amName: z.string().nullable(),
  submittedAt: z.string(),
});

export const paginatedInstituteSubmissionsSchema = z.object({
  data: z.array(instituteSubmissionListItemSchema),
  meta: paginationMetaSchema,
});

export const instituteSubmissionSummarySchema = z.object({
  total: z.number(),
  pending: z.number(),
  approved: z.number(),
  rejected: z.number(),
});

export const instituteValuesSchema = z.object({
  name: nullableImportedTextSchema,
  address: z.string().nullable(),
  instituteTypeName: z.string().nullable(),
  instituteCategory: z.enum(INSTITUTE_CATEGORY).nullable(),
  instituteLevel: z.enum(INSTITUTE_LEVEL).nullable(),
  examBoardName: z.string().nullable(),
  districtName: z.string().nullable(),
  thanaName: z.string().nullable(),
  eiinNumber: nullableImportedTextSchema,
  contactName: nullableImportedTextSchema,
  contactNumber: nullableImportedTextSchema,
  totalStudents: z.number().nullable(),
  purchasePercentage: z.number().nullable(),
  academicClasses: z.array(lookupItemSchema),
});

const submissionDetailBaseSchema = z.object({
  id: z.coerce.number(),
  status: z.enum(SUBMISSION_STATUS),
  code: z.string().nullable(),
  userId: z.coerce.number(),
  userName: z.string(),
  hrEmpId: z.string().nullable(),
  amName: z.string().nullable(),
  submittedAt: z.string(),
  current: instituteValuesSchema,
  photoUrl: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  capturedAt: z.string().nullable(),
  storedLatitude: z.number().nullable(),
  storedLongitude: z.number().nullable(),
  reviewedByName: z.string().nullable(),
  reviewerComment: z.string().nullable(),
  reviewedAt: z.string().nullable(),
});

const updateSubmissionDetailSchema = submissionDetailBaseSchema.extend({
  submissionType: z.literal(UPDATE_SUBMISSION_TYPE),
  proposed: instituteValuesSchema,
});

const removalSubmissionDetailSchema = submissionDetailBaseSchema.extend({
  submissionType: z.enum(REMOVAL_SUBMISSION_TYPES),
  reasonCode: z.enum(REMOVAL_REASON).nullable(),
  reasonComment: z.string().nullable(),
  proposed: z.null(),
});

export const instituteSubmissionDetailSchema = z.discriminatedUnion('submissionType', [
  updateSubmissionDetailSchema,
  removalSubmissionDetailSchema,
]);

export type InstituteSubmissionSearch = z.infer<typeof instituteSubmissionSearchSchema>;
export type InstituteSubmissionListItem = z.infer<typeof instituteSubmissionListItemSchema>;
export type PaginatedInstituteSubmissions = z.infer<typeof paginatedInstituteSubmissionsSchema>;
export type InstituteSubmissionSummary = z.infer<typeof instituteSubmissionSummarySchema>;
export type InstituteValues = z.infer<typeof instituteValuesSchema>;
export type InstituteSubmissionDetail = z.infer<typeof instituteSubmissionDetailSchema>;
export type RemovalSubmissionDetail = z.infer<typeof removalSubmissionDetailSchema>;

export interface InstituteSubmissionSummaryParams {
  submissionType?: SubmissionType | undefined;
  instituteTypeId?: number | undefined;
  districtId?: number | undefined;
  thanaId?: number | undefined;
  amUserId?: number | undefined;
  zmUserId?: number | undefined;
  dateFrom?: string | undefined;
  dateTo?: string | undefined;
  search?: string | undefined;
}

export interface InstituteSubmissionListParams extends InstituteSubmissionSummaryParams {
  status?: SubmissionStatus | undefined;
  pageSize: number;
}

export type InstituteSubmissionCountsBySubmitterParams = Omit<
  InstituteSubmissionListParams,
  'amUserId' | 'zmUserId' | 'pageSize'
>;
